import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CommandMenu = ({ 
  isVisible, 
  commands, 
  onSelectCommand, 
  activeIndex, 
  setActiveIndex,
  isDarkMode,
  language
}) => {
  const menuRef = useRef(null);
  const isMobile = useRef(window.innerWidth < 768);
  const isSmallMobile = useRef(window.innerWidth < 375);
  
  // Track window resize for mobile detection - optimize with debounce
  useEffect(() => {
    const handleResize = () => {
      isMobile.current = window.innerWidth < 768;
      isSmallMobile.current = window.innerWidth < 375;
    };
    
    // Debounce resize events
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', debouncedResize);
    handleResize(); // Call once initially
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);
  
  // Scroll active item into view when activeIndex changes
  useEffect(() => {
    if (isVisible && menuRef.current) {
      const activeItem = menuRef.current.querySelector(`[data-index="${activeIndex}"]`);
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isVisible]);

  // Translations for supported languages
  const getTranslation = (text) => {
    if (language === 'sr') {
      const srTranslations = {
        "Create an image of": "Kreiraj sliku",
        "Translate to": "Prevedi na",
        "Explain": "Objasni",
        "Write": "Napiši",
        "Summarize": "Sumiraj",
        "Analyze": "Analiziraj",
        "Compare": "Uporedi",
        "Help me": "Pomozi mi",
        "Brainstorm": "Razmisli o",
        "Fix grammar": "Ispravi gramatiku",
        "Commands": "Komande"
      };
      return srTranslations[text] || text;
    } 
    else if (language === 'ru') {
      const ruTranslations = {
        "Create an image of": "Создать изображение",
        "Translate to": "Перевести на",
        "Explain": "Объяснить",
        "Write": "Написать",
        "Summarize": "Обобщить",
        "Analyze": "Анализировать",
        "Compare": "Сравнить",
        "Help me": "Помогите мне",
        "Brainstorm": "Мозговой штурм",
        "Fix grammar": "Исправить грамматику",
        "Commands": "Команды"
      };
      return ruTranslations[text] || text;
    }
    else if (language === 'ar') {
      const arTranslations = {
        "Create an image of": "إنشاء صورة",
        "Translate to": "ترجمة إلى",
        "Explain": "شرح",
        "Write": "كتابة",
        "Summarize": "تلخيص",
        "Analyze": "تحليل",
        "Compare": "مقارنة",
        "Help me": "ساعدني",
        "Brainstorm": "عصف ذهني",
        "Fix grammar": "تصحيح القواعد",
        "Commands": "الأوامر"
      };
      return arTranslations[text] || text;
    }
    
    return text;
  };

  // Check if the current language is RTL
  const isRTL = language === 'ar';

  // No need to render if not visible
  if (!isVisible) return null;
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.15 }}
          className={`absolute left-2 xs:left-3 sm:left-4 bottom-full mb-1 xs:mb-1.5 sm:mb-2 max-h-60 sm:max-h-80 overflow-y-auto rounded-lg sm:rounded-xl shadow-xl z-50 w-[calc(100%-32px)] xs:w-[calc(100%-48px)] sm:w-[calc(100%-64px)] max-w-md`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: isDarkMode ? '#555 #2a2e33' : '#ccc #f5f5f5',
            background: isDarkMode ? 'rgba(32, 32, 32, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: isDarkMode ? '1px solid rgba(75, 75, 75, 0.5)' : '1px solid rgba(229, 231, 235, 0.7)',
            boxShadow: isDarkMode 
              ? '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.3)' 
              : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
            direction: isRTL ? 'rtl' : 'ltr'
          }}
        >
          <div className={`p-1.5 xs:p-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          } text-xs xs:text-sm font-medium ${isRTL ? 'text-right' : ''}`}>
            {getTranslation('Commands')}
          </div>
          <div className="py-0.5 xs:py-1">
            {commands.map((command, index) => (
              <div
                key={index}
                data-index={index}
                onClick={() => onSelectCommand(command)}
                className={`px-2 xs:px-3 py-1.5 xs:py-2 sm:py-2.5 flex items-center gap-2 xs:gap-3 cursor-pointer transition-colors ${
                  activeIndex === index 
                    ? (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50') 
                    : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50')
                } ${isRTL ? 'flex-row-reverse' : ''}`}
                style={{
                  borderBottom: index < commands.length - 1 
                    ? isDarkMode 
                      ? '1px solid rgba(75, 75, 75, 0.15)' 
                      : '1px solid rgba(229, 231, 235, 0.5)'
                    : 'none'
                }}
              >
                <div className={`w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <i className={`${command.icon} text-xs xs:text-sm ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-500'
                  }`}></i>
                </div>
                <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                  <div className={`font-medium text-xs xs:text-sm truncate ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {getTranslation(command.title)}
                  </div>
                  <div className={`text-[10px] xs:text-xs truncate ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {isSmallMobile.current 
                      ? command.description.split(' ').slice(0, 4).join(' ') + (command.description.split(' ').length > 4 ? '...' : '')
                      : command.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Custom xs breakpoint */}
          <style jsx>{`
            @media (min-width: 400px) {
              .xs\\:left-3 {
                left: 0.75rem;
              }
              .xs\\:mb-1\\.5 {
                margin-bottom: 0.375rem;
              }
              .xs\\:p-2 {
                padding: 0.5rem;
              }
              .xs\\:py-1 {
                padding-top: 0.25rem;
                padding-bottom: 0.25rem;
              }
              .xs\\:px-3 {
                padding-left: 0.75rem;
                padding-right: 0.75rem;
              }
              .xs\\:py-2 {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
              }
              .xs\\:gap-3 {
                gap: 0.75rem;
              }
              .xs\\:w-7 {
                width: 1.75rem;
              }
              .xs\\:h-7 {
                height: 1.75rem;
              }
              .xs\\:text-sm {
                font-size: 0.875rem;
                line-height: 1.25rem;
              }
              .xs\\:text-xs {
                font-size: 0.75rem;
                line-height: 1rem;
              }
              .xs\\:w-\\[calc\\(100\\%-48px\\)\\] {
                width: calc(100% - 48px);
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandMenu; 